import React, { createContext, useContext, useEffect, useState } from "react";
import { createBrowserRouter, Navigate, NavLink, Outlet, RouterProvider, useLocation, useNavigate } from "react-router";
import { Badge, BrandMark, Button, Card, Field, Icon } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";
import { adminApi } from "./api";
import { overallHealth, useSystemState } from "./hooks";
import { healthTone, healthText } from "./components";
import { DialogsProvider } from "./dialogs";
import { AdminNotificationBell, AdminNotificationsPanel, useAdminNotifications } from "./notifications";
import { GlobalSearchOverlay } from "./pages/GlobalSearch";
import { SetupWizardPage } from "./pages/SetupWizard";
import { OverviewPage } from "./pages/Overview";
import { UsersPage } from "./pages/Users";
import { SubscriptionsPage } from "./pages/Subscriptions";
import { ProductsPage } from "./pages/Products";
import { FeedbackPage } from "./pages/Feedback";
import { FeedbackChatPage } from "./pages/FeedbackChat";
import { WebsitePage } from "./pages/Website";
import { PrivacyPage } from "./pages/Privacy";
import { SystemPage } from "./pages/System";

interface AdminUser { id: string; email: string; displayName: string; role: string; }
interface AdminAuthValue { user: AdminUser | null; loading: boolean; login: (email: string, password: string) => Promise<void>; logout: () => Promise<void>; }
const AdminAuth = createContext<AdminAuthValue | null>(null);

function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    adminApi<AdminUser>("/auth/me")
      .then((me) => { if (!cancelled) setUser(me); })
      .catch(() => { if (!cancelled) setUser(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);
  const login = async (email: string, password: string) => {
    const result = await adminApi<{ user: AdminUser }>("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
    setUser(result.user);
  };
  const logout = async () => {
    await adminApi("/auth/logout", { method: "POST" }).catch(() => undefined);
    setUser(null);
  };
  return <AdminAuth.Provider value={{ user, loading, login, logout }}>{children}</AdminAuth.Provider>;
}

function useAdminAuth() {
  const value = useContext(AdminAuth);
  if (!value) throw new Error("Admin auth missing");
  return value;
}

function AdminLogin() {
  const { t } = useI18n();
  const { login } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      navigate("/", { replace: true });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : t("common.error"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="admin-login">
      <Card className="admin-login__card" elevated>
        <div className="admin-login__brand"><BrandMark /><Badge tone="purple">{t("admin.ownerOnly")}</Badge></div>
        <h1>{t("admin.title")}</h1>
        <p>{t("admin.login.private")}</p>
        <form onSubmit={submit}>
          <Field label={t("auth.email")} type="email" value={email} autoComplete="username" required onChange={(event) => setEmail(event.target.value)} />
          <Field label={t("auth.password")} type="password" value={password} autoComplete="current-password" required onChange={(event) => setPassword(event.target.value)} />
          {error && <div className="admin-error" role="alert">{error}</div>}
          <Button type="submit" icon="lock" loading={busy}>{t("auth.signIn")}</Button>
        </form>
      </Card>
    </main>
  );
}

/* Wix-Studio-style grouped navigation (Atria checklist): each group is one
   studio domain; items map 1:1 to the existing routes. */
import type { IconName } from "@nelurio/ui";

const navGroups: Array<{ groupKey: string; items: Array<{ to: string; icon: IconName; key: string }> }> = [
  { groupKey: "admin.group.studio", items: [{ to: "/", icon: "chart", key: "admin.overview" }] },
  { groupKey: "admin.group.audience", items: [{ to: "/users", icon: "users", key: "admin.users" }, { to: "/subscriptions", icon: "credit-card", key: "admin.subscriptions" }] },
  { groupKey: "admin.group.commerce", items: [{ to: "/products", icon: "library", key: "admin.products" }] },
  { groupKey: "admin.group.inbox", items: [{ to: "/feedback", icon: "message", key: "admin.feedback" }] },
  { groupKey: "admin.group.website", items: [{ to: "/website", icon: "globe", key: "admin.website" }] },
  { groupKey: "admin.group.trust", items: [{ to: "/privacy", icon: "shield", key: "admin.privacy" }] },
  { groupKey: "admin.group.system", items: [{ to: "/system", icon: "server", key: "admin.system" }] }
];
const nav = navGroups.flatMap((group) => group.items);

function AdminShell() {
  const { t, locale, setLocale } = useI18n();
  const { user, logout } = useAdminAuth();
  const location = useLocation();
  const system = useSystemState();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const notifications = useAdminNotifications(notifOpen);
  const overall = overallHealth([system.data?.api, system.data?.database, system.data?.worker]);

  // Close the mobile menu and the notification panel on route change.
  useEffect(() => { setMenuOpen(false); setNotifOpen(false); }, [location.pathname]);

  // Command palette shortcut: Ctrl/Cmd + K anywhere in the console.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen((open) => !open);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Escape closes the mobile menu.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  // Mobile-only: flat 2-column grid of all nav items (no group label wall);
  // the group stays available as a `title` hint on each tile.
  const mobileNavLinks = (onNavigate: () => void) => navGroups.flatMap((group) =>
    group.items.map((item) => ({ item, groupLabel: t(group.groupKey) }))
  ).map(({ item, groupLabel }) => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.to === "/"}
      onClick={onNavigate}
      className={({ isActive }) => `admin-nav-link ${isActive ? "is-active" : ""}`}
      title={groupLabel}
    >
      <Icon name={item.icon} />
      <span>{t(item.key)}</span>
    </NavLink>
  ));

  const navLinks = (onNavigate: () => void) => navGroups.map((group) => (
    <div className="admin-nav-group" key={group.groupKey}>
      <span className="admin-nav-group__label">{t(group.groupKey)}</span>
      {group.items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === "/"}
          onClick={onNavigate}
          className={({ isActive }) => `admin-nav-link ${isActive ? "is-active" : ""}`}
        >
          <Icon name={item.icon} />
          <span>{t(item.key)}</span>
        </NavLink>
      ))}
    </div>
  ));

  const userBlock = (compact: boolean) => (
    <div className="admin-user">
      <span className="admin-user__avatar" aria-hidden="true">{(user?.displayName || "?").slice(0, 1).toUpperCase()}</span>
      {!compact && (
        <span className="admin-user__meta">
          <strong>{user?.displayName}</strong>
          <small>{user?.email}</small>
        </span>
      )}
    </div>
  );

  // Live owner events: one bell per header. The mobile header and the desktop
  // topbar are mutually exclusive via CSS, so both share the same hook state.
  const bell = (
    <AdminNotificationBell
      unread={notifications.unread}
      open={notifOpen}
      onToggle={() => setNotifOpen((open) => !open)}
    >
      <AdminNotificationsPanel
        items={notifications.items}
        loaded={notifications.loaded}
        unread={notifications.unread}
        onMarkAllRead={() => void notifications.markAllRead()}
        onMarkSomeRead={(ids) => void notifications.markSomeRead(ids)}
        onAfterNavigate={() => setNotifOpen(false)}
      />
    </AdminNotificationBell>
  );

  const langToggle = (
    <button
      type="button"
      className="admin-lang-btn"
      onClick={() => setLocale(locale === "de-DE" ? "fa-IR" : "de-DE")}
      aria-label={t("admin.shell.langAria")}
      title={t("admin.shell.langAria")}
    >
      {locale === "de-DE" ? "فا" : "DE"}
    </button>
  );

  const searchButton = (
    <button
      type="button"
      className="admin-lang-btn"
      onClick={() => setSearchOpen(true)}
      aria-label={t("admin.search.aria")}
      title={t("admin.search.title")}
    >
      <Icon name="search" size={18} />
    </button>
  );

  const healthBannerText = overall === "checking"
    ? t("admin.shell.healthChecking")
    : overall === "healthy"
      ? t("admin.shell.healthHealthy")
      : overall === "mixed"
        ? t("admin.shell.healthMixed")
        : t("admin.shell.healthDown");

  return (
    <div className="admin-shell">
      <a className="admin-skip" href="#admin-main">{t("admin.shell.skipToContent")}</a>

      {/* Mobile header with burger; hidden on desktop via CSS. */}
      <header className="admin-header">
        <div className="admin-header__brand">
          <BrandMark compact />
          <span className="admin-header__title">{t("admin.title")}</span>
          <i className={`admin-health-dot is-${healthTone(overall === "checking" ? "unknown" : overall === "healthy" ? "healthy" : "degraded")}`} aria-hidden="true" />
        </div>
        <div className="admin-header__actions">
          {searchButton}
          {langToggle}
          {bell}
          <button
            type="button"
            className="admin-header__user"
            aria-label={menuOpen ? t("admin.shell.accountMenuClose") : t("admin.shell.accountMenuOpen")}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span className="admin-user__avatar" aria-hidden="true">{(user?.displayName || "?").slice(0, 1).toUpperCase()}</span>
          </button>
          <button
            type="button"
            className="admin-burger"
            aria-expanded={menuOpen}
            aria-controls="admin-mobile-menu"
            aria-label={menuOpen ? t("admin.shell.menuClose") : t("admin.shell.menuOpen")}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <Icon name={menuOpen ? "x" : "menu"} size={22} />
          </button>
        </div>
      </header>
      {menuOpen && <div className="admin-menu-backdrop" onClick={() => setMenuOpen(false)} aria-hidden="true" />}
      <div id="admin-mobile-menu" className={`admin-menu ${menuOpen ? "is-open" : ""}`}>
        <nav aria-label={t("admin.shell.navAriaLabel")}><div className="admin-menu__grid">{mobileNavLinks(() => setMenuOpen(false))}</div></nav>
        <div className="admin-menu__footer">
          {userBlock(false)}
          <Button variant="ghost" icon="logout" onClick={() => void logout()}>{t("admin.shell.logout")}</Button>
        </div>
      </div>

      {/* Desktop sidebar; hidden below 900px via CSS. */}
      <aside className="admin-sidebar">
        <div className="admin-sidebar__brand"><BrandMark /><Badge tone="purple">{t("admin.shell.private")}</Badge></div>
        <nav aria-label={t("admin.shell.navAriaLabel")}>{navLinks(() => undefined)}</nav>
        <div className="admin-sidebar__bottom">
          {userBlock(false)}
          <button type="button" className="admin-sidebar__logout" title={t("admin.shell.logout")} aria-label={t("admin.shell.logout")} onClick={() => void logout()}>
            <Icon name="logout" size={18} />
          </button>
        </div>
      </aside>

      <div className="admin-stage">
        <header className="admin-topbar">
          <div className="admin-topbar__heading"><strong>{location.pathname.startsWith("/setup") ? t("admin.setup.title") : t(nav.find((item) => item.to === location.pathname)?.key ?? "admin.overview")}</strong></div>
          <div className="admin-topbar__actions">
            {searchButton}
            {langToggle}
            {bell}
            <span className={`system-pill ${overall !== "healthy" ? "is-warn" : ""}`}><i aria-hidden="true" />{healthBannerText}</span>
          </div>
        </header>
        <main id="admin-main" className="admin-main">
          <Outlet />
        </main>
      </div>
      <GlobalSearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}

function Protected() {
  const { user, loading } = useAdminAuth();
  const { t } = useI18n();
  if (loading) {
    return (
      <div className="admin-login">
        <Card className="admin-login__card" elevated>
          <BrandMark />
          <p className="admin-login__loading">{t("common.loading")}</p>
        </Card>
      </div>
    );
  }
  return user ? <AdminShell /> : <Navigate to="/login" replace />;
}

const router = createBrowserRouter([
  { path: "/login", element: <AdminLogin /> },
  {
    element: <Protected />,
    children: [
      { index: true, element: <OverviewPage /> },
      { path: "products", element: <ProductsPage /> },
      { path: "users", element: <UsersPage /> },
      { path: "subscriptions", element: <SubscriptionsPage /> },
      { path: "feedback", element: <FeedbackPage /> },
      { path: "feedback/chat", element: <FeedbackChatPage /> },
      { path: "setup", element: <SetupWizardPage /> },
      { path: "website", element: <WebsitePage /> },
      { path: "privacy", element: <PrivacyPage /> },
      { path: "system", element: <SystemPage /> },
      { path: "*", element: <Navigate to="/" replace /> }
    ]
  }
]);

export function AdminApp() {
  return (
    <AdminAuthProvider>
      <DialogsProvider>
        <RouterProvider router={router} />
      </DialogsProvider>
    </AdminAuthProvider>
  );
}
